import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { fileToCompressedDataUrl } from "@/lib/image";
import PhotoEditor from "@/components/PhotoEditor";
import Logo from "@/components/Logo";
import CategorySelect from "@/components/CategorySelect";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { countryFlag, countryName } from "@/lib/countries";
import { useLang } from "@/context/LanguageContext";
import { LostAuthGate } from "@/components/LostAuthGate";
import { FormHeader } from "@/components/FormShell";
import SubmitOverlay from "@/components/SubmitOverlay";
import { CheckCircle2, Search, ArrowRight, ImagePlus, Camera, X, Undo2, Pencil, History } from "lucide-react";
import { IdentityFields, ItemDetailFields, EXTRA_DEFAULTS, extraPayload } from "@/components/DeclarationFields";
import { FindoSpinner } from "@/components/FindoSpinner";

const PrefillHint = () => {
  const { t } = useLang();
  return (
  <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-medium text-findo-indigo/80 align-middle" data-testid="prefill-hint">
    <Undo2 size={11} /> {t("hc.takenFromYourDeclaration")}
  </span>
  );
};

export default function Declare() {
  const { partnerId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const initialPhotos = location.state?.photos || [];
  const establishmentId = searchParams.get("e") || null;
  // When the owner reached this form from the cross-match gate (/objet-trouve),
  // the object was already confirmed there — skip the redundant "/verifier" step.
  const alreadyVerified = searchParams.get("verified") === "1";
  const { t, lang } = useLang();  const [partner, setPartner] = useState(null);
  const { user: b2cUser, loading: b2cLoading } = useB2C();
  const prefilledRef = useRef(false);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [dirty, setDirty] = useState(false);
  const draftLoadedRef = useRef(false);
  const draftKey = `findo_declare_draft_${partnerId || ""}${establishmentId ? ":" + establishmentId : ""}`;
  const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 jours
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const galleryRef2 = useRef(null);
  const cameraRef2 = useRef(null);
  const submittingRef = useRef(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorTarget, setEditorTarget] = useState("image_base64");
  const [form, setForm] = useState(() => ({
    category: searchParams.get("cat") || "", description: searchParams.get("desc") || "", lost_date: new Date().toISOString().slice(0, 10),
    lost_place: searchParams.get("place") || "", customer_name: "", customer_email: "", customer_phone: "",
    image_base64: initialPhotos[0] || null, image_base64_2: initialPhotos[1] || null,
    ...EXTRA_DEFAULTS,
  }));

  // Pre-fill personal contact fields from the connected B2C profile (once the
  // profile is available). Every field stays fully editable afterwards.
  useEffect(() => {
    if (prefilledRef.current || !b2cUser) return;
    prefilledRef.current = true;
    setForm((f) => ({
      ...f,
      customer_name: f.customer_name || b2cUser.name || "",
      customer_email: f.customer_email || b2cUser.email || "",
      customer_phone: f.customer_phone || b2cUser.phone || "",
      owner_first_name: f.owner_first_name || b2cUser.first_name || "",
      owner_last_name: f.owner_last_name || b2cUser.last_name || "",
      city: f.city || b2cUser.city || "",
    }));
  }, [b2cUser]);

  // Discreet "prefilled from your declaration/profile" markers — shown only
  // while the field still holds the pre-filled value (hidden once edited).
  const descHint = !!searchParams.get("desc") && form.description === searchParams.get("desc");
  const emailHint = !!b2cUser && !!form.customer_email && form.customer_email === b2cUser.email;
  const phoneHint = !!b2cUser && !!form.customer_phone && form.customer_phone === b2cUser.phone;
  const photoHint = initialPhotos.length > 0 && !!form.image_base64 && form.image_base64 === initialPhotos[0];

  useEffect(() => {
    const eq = establishmentId ? `?e=${establishmentId}` : "";
    setNotFound(false);
    setLoadError(false);
    api(`/partners/${partnerId}/public${eq}`, { auth: false })
      .then(setPartner)
      .catch((err) => {
        // A genuine 404 means the partner (or site) truly doesn't exist.
        // Any other failure (network/5xx/timeout) is transient — don't dead-end
        // the user on a misleading "Partenaire introuvable" screen.
        if (err?.status === 404) setNotFound(true);
        else setLoadError(true);
      });
  }, [partnerId, establishmentId, reloadKey]);

  const set = (k) => (e) => { setDirty(true); setForm((f) => ({ ...f, [k]: e.target?.value ?? e })); };

  // ---- Draft persistence: resume a half-finished declaration ----
  useEffect(() => {
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        // Auto-expire stale drafts so we never restore an outdated declaration.
        if (d && d._savedAt && Date.now() - d._savedAt > DRAFT_TTL_MS) {
          localStorage.removeItem(draftKey);
        } else if (d && (d.description || d.customer_name || d.customer_email || d.customer_phone || d.image_base64)) {
          const { _savedAt, ...fields } = d;
          setForm((f) => ({ ...f, ...fields }));
          setDraftRestored(true);
        }
      }
    } catch { /* ignore corrupt draft */ }
  }, [draftKey]);

  useEffect(() => {
    if (!dirty) return;
    const payload = { ...form, _savedAt: Date.now() };
    try {
      localStorage.setItem(draftKey, JSON.stringify(payload));
    } catch {
      // Quota (large photos) — persist text fields only.
      try {
        const { image_base64, image_base64_2, ...rest } = payload;
        localStorage.setItem(draftKey, JSON.stringify(rest));
      } catch { /* give up silently */ }
    }
  }, [form, dirty, draftKey]);

  function discardDraft() {
    try { localStorage.removeItem(draftKey); } catch { /* noop */ }
    setDraftRestored(false);
    setDirty(false);
    setForm({
      category: searchParams.get("cat") || "", description: searchParams.get("desc") || "",
      lost_date: new Date().toISOString().slice(0, 10), lost_place: searchParams.get("place") || "",
      customer_name: b2cUser?.name || "", customer_email: b2cUser?.email || "", customer_phone: b2cUser?.phone || "",
      image_base64: initialPhotos[0] || null, image_base64_2: initialPhotos[1] || null,
    });
  }


  async function onPhoto(e, slot = "image_base64") {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const compressed = await fileToCompressedDataUrl(f, { maxDim: 2000, quality: 0.9 });
      setEditorTarget(slot);
      setEditorSrc(compressed);
    } catch (err) {
      toast.error(t("pub.cantProcessImage"));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!consent) { toast.error(t("consent.required")); return; }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await api("/declarations", {
        method: "POST", auth: false,
        body: { partner_id: partnerId, establishment_id: establishmentId, ...form, ...extraPayload(form), customer_name: extraPayload(form).owner_name, lang, share_consent: true,
          customer_email: form.customer_email.trim().toLowerCase() },
      });
      setDone(res);
      // Cross-match path: identity already confirmed on the object card → persist
      // it so the recovery flow never re-asks « Est-ce bien votre objet ? ».
      if (alreadyVerified && res?.id && res?.matched_item_id) {
        api(`/declarations/${res.id}/confirm-identity`, { method: "POST", auth: false }).catch(() => {});
      }
      try { localStorage.removeItem(draftKey); } catch { /* noop */ }
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'envoi");
    } finally { setLoading(false); submittingRef.current = false; }
  }

  if (!b2cLoading && !b2cUser) return <LostAuthGate t={t} />;

  if (loadError && !partner) {
    return (
      <div className="max-w-md mx-auto py-32 text-center" data-testid="declare-load-error">
        <p className="text-slate-500">{t("dec.loadError") === "dec.loadError" ? "Impossible de charger le partenaire pour le moment. Vérifiez votre connexion et réessayez." : t("dec.loadError")}</p>
        <Button onClick={() => setReloadKey((k) => k + 1)} className="mt-6 rounded-full font-semibold" data-testid="declare-retry-btn">
          {t("dec.retry") === "dec.retry" ? "Réessayer" : t("dec.retry")}
        </Button>
      </div>
    );
  }

  if (notFound) {
    return <div className="max-w-md mx-auto py-32 text-center"><p className="text-slate-500">{t("dec.notfound")}</p><Link to="/" className="text-findo-indigo font-semibold">{t("dec.home")}</Link></div>;
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-6 py-20 text-center animate-fade-up" data-testid="declare-success">
        <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-teal-100 text-teal-700"><CheckCircle2 size={34} /></div>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-900">{t("dec.successTitle")}</h1>
        <p className="mt-2 text-slate-600">{t("dec.successText")} <b>{partner?.org_name}</b>.</p>
        {done.matched_item_id && (
          <div className="mt-5 rounded-xl bg-teal-50 border border-teal-200 p-4 text-teal-800 text-sm font-semibold flex items-center justify-center gap-2">
            <Search size={16} /> {t("dec.matchFound")}
          </div>
        )}
        <div className="mt-6 flex flex-col gap-3">
          <Link to={`/track/${done.id}`}><Button className="w-full rounded-xl font-semibold" data-testid="declare-track-btn">{t("dec.trackBtn")} <ArrowRight size={16} className="ml-1.5" /></Button></Link>
          {done.matched_item_id && <Link to={alreadyVerified ? `/pay/${done.id}` : `/verifier/${done.id}`}><Button variant="outline" className="w-full rounded-xl font-semibold">{t("dec.recoverBtn")}</Button></Link>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="relative rounded-[1.75rem] overflow-hidden card-shadow mb-8 photo-scrim">
        <img src={partner?.cover_base64 || IMAGES.reception} alt="" className="w-full h-56 object-cover" />
        <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8 text-white">
          <span className="inline-flex self-start items-center gap-2 rounded-full badge-red px-3 py-1 text-xs font-bold uppercase tracking-wide">{t("dec.badge")}</span>
          <div className="mt-4 flex items-center gap-3">
            {partner?.logo_base64 ? (
              <img src={partner.logo_base64} alt="" className="h-14 w-14 rounded-2xl object-cover border-2 border-white/50" />
            ) : (
              <Logo size={48} />
            )}
            <div>
              <h1 className="font-display text-3xl font-semibold leading-none">{partner?.org_name || "…"}</h1>
              {partner?.country && <p className="text-white/80 text-sm mt-1">{countryFlag(partner.country)} {countryName(partner.country)}</p>}
            </div>
          </div>
        </div>
      </div>

      <SubmitOverlay show={loading} />
      <form onSubmit={submit} className="findo-form form-card space-y-6 bg-white rounded-3xl border border-border overflow-hidden p-6" data-testid="declare-form">
        <FormHeader icon={Search} title={t("dec.badge")} subtitle={partner?.org_name} className="-mx-6 -mt-6 mb-2" />
        {draftRestored && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="draft-restored-banner">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><History size={14} /> {t("hc.draftRestoredPickUpYour")}</p>
            <button type="button" onClick={discardDraft} className="text-xs font-semibold text-amber-700 hover:underline shrink-0" data-testid="draft-discard-btn">{t("hc.clear")}</button>
          </div>
        )}
        <div><Label className="mb-1.5 block">{t("dec.category")} *</Label><CategorySelect value={form.category} onChange={set("category")} testid="declare-category" /></div>
        <div>
          <Label className="mb-1.5 block">{t("dec.description")} *{descHint && <PrefillHint />}</Label>
          <Textarea required rows={3} maxLength={500} value={form.description} onChange={set("description")}
            placeholder={(() => { const l = t("dec.descPhLong"); return l === "dec.descPhLong" ? t("hc.describeTheItemBrandColor") : l; })()} className="rounded-xl resize-none" data-testid="declare-description" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="mb-1.5 block">{t("dec.date")} *</Label><Input type="date" required value={form.lost_date} onChange={set("lost_date")} className="h-11 rounded-xl" data-testid="declare-date" /></div>
          <div><Label className="mb-1.5 block">{t("dec.place")} *</Label><Input required value={form.lost_place} onChange={set("lost_place")} placeholder={t("dec.placePh")} className="h-11 rounded-xl" data-testid="declare-place" /></div>
        </div>
        <ItemDetailFields form={form} setForm={setForm} prefix="declare" />
        <div>
          <Label className="mb-1.5 block">{t("dep.photo")} <span className="text-slate-400 font-normal">{t("dep.optional")}</span>{photoHint && <PrefillHint />}</Label>
          {form.image_base64 ? (
            <div className="relative w-full" data-testid="declare-photo-preview">
              <img src={form.image_base64} alt={t("pub.previewAlt")} className="w-full h-48 object-cover rounded-xl border border-border" />
              <button type="button" onClick={() => { setDirty(true); setForm((s) => ({ ...s, image_base64: null })); }}
                className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80" data-testid="declare-photo-remove">
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button type="button" onClick={() => galleryRef.current?.click()}
                className="flex items-center justify-center gap-2 h-12 rounded-xl border border-dashed border-border text-sm font-semibold text-slate-600 hover:border-findo-indigo hover:text-findo-indigo transition-colors" data-testid="declare-photo-gallery">
                <ImagePlus size={18} /> {t("hc.gallery")}
              </button>
              <button type="button" onClick={() => cameraRef.current?.click()}
                className="flex items-center justify-center gap-2 h-12 rounded-xl border border-dashed border-border text-sm font-semibold text-slate-600 hover:border-findo-indigo hover:text-findo-indigo transition-colors" data-testid="declare-photo-camera">
                <Camera size={18} /> {t("hc.takeAPhoto")}
              </button>
            </div>
          )}
          <input ref={galleryRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="declare-photo-gallery-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="declare-photo-camera-input" />
          {form.image_base64 && (
            <div className="mt-3" data-testid="declare-photo2-block">
              <Label className="mb-1.5 block text-sm text-slate-500">{t("pub.addSecondPhoto")}</Label>
              {form.image_base64_2 ? (
                <div className="relative w-full" data-testid="declare-photo2-preview">
                  <img src={form.image_base64_2} alt={t("pub.previewAlt")} className="w-full h-48 object-cover rounded-xl border border-border" />
                  <button type="button" onClick={() => { setDirty(true); setForm((s) => ({ ...s, image_base64_2: null })); }}
                    className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80" data-testid="declare-photo2-remove">
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => galleryRef2.current?.click()}
                    className="flex items-center justify-center gap-2 h-12 rounded-xl border border-dashed border-border text-sm font-semibold text-slate-600 hover:border-findo-indigo hover:text-findo-indigo transition-colors" data-testid="declare-photo2-gallery">
                    <ImagePlus size={18} /> {t("hc.gallery")}
                  </button>
                  <button type="button" onClick={() => cameraRef2.current?.click()}
                    className="flex items-center justify-center gap-2 h-12 rounded-xl border border-dashed border-border text-sm font-semibold text-slate-600 hover:border-findo-indigo hover:text-findo-indigo transition-colors" data-testid="declare-photo2-camera">
                    <Camera size={18} /> {t("hc.takeAPhoto")}
                  </button>
                </div>
              )}
              <input ref={galleryRef2} type="file" accept="image/*" onChange={(e) => onPhoto(e, "image_base64_2")} className="hidden" data-testid="declare-photo2-gallery-input" />
              <input ref={cameraRef2} type="file" accept="image/*" capture="environment" onChange={(e) => onPhoto(e, "image_base64_2")} className="hidden" data-testid="declare-photo2-camera-input" />
            </div>
          )}
        </div>
        <div className="border-t border-border pt-5 space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-bold text-slate-700">{t("dec.contactTitle")}</p>
            {b2cUser && (
              <a href="/mon-compte" target="_blank" rel="noreferrer" className="text-xs font-semibold text-findo-indigo hover:underline inline-flex items-center gap-1" data-testid="fix-contact-link">
                <Pencil size={12} /> {t("hc.correctMyDetails")}
              </a>
            )}
          </div>
          <div className="sm:col-span-2"><IdentityFields form={form} setForm={setForm} prefix="declare" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label className="mb-1.5 block">{t("dec.email")} *{emailHint && <PrefillHint />}</Label><Input type="email" required value={form.customer_email} onChange={set("customer_email")} className="h-11 rounded-xl" data-testid="declare-email" /></div>
            <div><Label className="mb-1.5 block">{t("dec.phone")} *{phoneHint && <PrefillHint />}</Label><Input required value={form.customer_phone} onChange={set("customer_phone")} placeholder="+33 6 …" className="h-11 rounded-xl" data-testid="declare-phone" /></div>
          </div>
        </div>
        <label className="flex items-start gap-3 rounded-xl border border-border bg-slate-50 p-3.5 cursor-pointer" data-testid="consent-wrap">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required
            className="mt-0.5 h-4 w-4 shrink-0 accent-findo-navy" data-testid="consent-checkbox" />
          <span className="text-xs text-slate-600 leading-relaxed">{t("consent.share")}</span>
        </label>
        <Button type="submit" disabled={loading || !consent} className="w-full h-12 rounded-xl font-semibold text-base" data-testid="declare-submit">
          {loading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("dec.submitting")}</span> : t("dec.submit")}
        </Button>
      </form>
      <PhotoEditor src={editorSrc} open={!!editorSrc}
        onClose={() => setEditorSrc(null)}
        onSave={(b64) => { setDirty(true); setForm((s) => ({ ...s, [editorTarget]: b64 })); }} />
    </div>
  );
}
